import { createContextApi, invalidatesOne, providesList, providesOne, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';
import { createContext, useState, useCallback, useMemo, createElement, useContext } from 'react';

// src/index.ts
var LEDGER_TAGS = [
  "LedgerEntry",
  "LedgerAccount",
  "Statement",
  "SuspenseCase",
  "VaultReconciliation",
  "VaultStatement",
  "VaultPosition",
  "BankStatement",
  "BankReconciliation",
  "DailyVolume"
];
var LEDGER_MAX_PAGE_SIZE = 100;
var LEDGER_DEFAULT_PAGE_SIZE = 50;
var LEDGER_DEFAULT_RECONCILIATION_LIMIT = 30;
function queryParams(entries) {
  const params = {};
  for (const [key, value] of Object.entries(entries)) {
    if (value !== void 0 && value !== null && value !== "") params[key] = value;
  }
  return params;
}
function mergeContinuationPage(loaded, page, key) {
  const arriving = new Set(page.map(key));
  const overlapAt = loaded.findIndex((row) => arriving.has(key(row)));
  if (overlapAt >= 0) loaded.splice(overlapAt);
  loaded.push(...page);
}
function cursorPagedEndpoint(key) {
  const paging = createCursorPagedEndpoint();
  return {
    ...paging,
    merge: (currentCache, incoming, meta) => {
      if (!meta.arg?.cursor) {
        paging.merge(currentCache, incoming, meta);
        return;
      }
      mergeContinuationPage(currentCache.items, incoming.items, key);
      currentCache.pagination = incoming.pagination;
    }
  };
}
var ledgerEndpoints = (build) => ({
  listEntries: build.query({
    query: ({ pageSize, cursor, accountType, unit, sourceType, status, search, fromDate, toDate }) => ({
      url: "/ledger/entries",
      params: queryParams({ pageSize, cursor, accountType, unit, sourceType, status, search, fromDate, toDate })
    }),
    ...cursorPagedEndpoint((entry) => entry.entryId),
    providesTags: (result) => providesList("LedgerEntry", result, "entryId"),
    keepUnusedDataFor: 120
  }),
  /**
   * The same journal read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listEntries} drops `cursor` from its cache key and appends every response into a
   * single entry, which is exactly right for a "load more" feed and exactly wrong for a numbered
   * pager: paging forward would grow the rendered set instead of replacing it, and paging *back*
   * would re-fetch a walked cursor and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * *Previous* free — a page already visited is answered from cache rather than re-fetched.
   * Both shapes stay published because both are legitimate, and this is the same pair, for the
   * same stated reason, as api-audit's `listEntries` / `listEntriesPage`.
   *
   * **`pagination.total` is `-1` here, normalised to `null`, on every page.**
   * `LedgerEntryQueryRepositoryImpl#search` declines the count deliberately — it is a
   * three-table `COUNT(*)` over the tenant's entire journal with no bound to appeal to — so a
   * pager on this endpoint shows the lower bound `pagerTotalCount` computes and says in words
   * that it is one. The statement reads below are the bounded ones and do answer a real number.
   */
  listEntriesPage: build.query({
    query: ({ pageSize, cursor, accountType, unit, sourceType, status, search, fromDate, toDate }) => ({
      url: "/ledger/entries",
      params: queryParams({ pageSize, cursor, accountType, unit, sourceType, status, search, fromDate, toDate })
    }),
    providesTags: (result) => providesList("LedgerEntry", result, "entryId"),
    keepUnusedDataFor: 120
  }),
  /** The balanced transaction behind an entry — legs, INR value, locked rate, payout ref. */
  getTransaction: build.query({
    query: (transactionId) => ({ url: `/ledger/transactions/${transactionId}` }),
    providesTags: (_r, _e, id) => providesOne("LedgerEntry", id)
  }),
  /**
   * Returns the **reversing** transaction (a fresh id with contra postings), not
   * the reversed one — so a caller that wants to show what happened reads the
   * response rather than re-fetching the original.
   *
   * Keyed on the reversed transaction id: a transaction reverses exactly once,
   * and `TransactionStatus.REVERSED` is terminal.
   */
  reverseTransaction: build.mutation({
    query: ({ transactionId, reason }) => ({
      url: `/ledger/transactions/${transactionId}/reverse`,
      method: "POST",
      body: { reason },
      idempotencyKey: `ledger-reverse:${transactionId}`
    }),
    // The contra postings move every leg's balance and land on the same
    // customer statement the original did — and the transaction id alone does
    // not say whose. So the whole statement family and the live position go.
    invalidatesTags: (_r, _e, { transactionId }) => [
      ...providesOne("LedgerEntry", transactionId),
      { type: "LedgerEntry", id: "LIST" },
      { type: "LedgerAccount", id: "LIST" },
      { type: "VaultPosition", id: "CURRENT" },
      { type: "Statement", id: "LIST" }
    ]
  }),
  /**
   * Accumulates by `nextCursor` like the paged endpoints, but hand-rolled because
   * the response is a bespoke envelope rather than `PagedApiResponse`.
   */
  listAccounts: build.query({
    query: ({ pageSize, cursor, unit }) => ({
      url: "/ledger/accounts",
      params: queryParams({ pageSize, cursor, unit })
    }),
    serializeQueryArgs: ({ queryArgs, endpointName }) => {
      const { cursor: _cursor, ...rest } = queryArgs ?? {};
      return `${endpointName}(${JSON.stringify(rest)})`;
    },
    merge: (currentCache, incoming, { arg }) => {
      if (!arg?.cursor) {
        Object.assign(currentCache, incoming);
        return;
      }
      mergeContinuationPage(currentCache.accounts, incoming.accounts, (account) => account.accountId);
      currentCache.nextCursor = incoming.nextCursor ?? null;
      currentCache.totalAccounts = incoming.totalAccounts;
      currentCache.totalHeld = incoming.totalHeld;
    },
    forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
    providesTags: (result) => providesList("LedgerAccount", result?.accounts, "accountId"),
    keepUnusedDataFor: 60
  }),
  /**
   * The same directory read, paged **discretely** — the cursor stays in the cache key, so each
   * page is its own entry and *Previous* is answered from cache. See {@link listEntriesPage} for
   * why both shapes exist.
   *
   * The envelope carries `totalAccounts`, a real count over every account in the book rather
   * than over the page, so a numbered pager here is exact — the footer's last page number is a
   * page that exists. It does **not** carry a `pagination` block, so a caller wiring a grid
   * reads `totalAccounts` for `totalCount` and `nextCursor != null` for "there is more".
   *
   * The endpoint binds `unit`, `cursor` and `pageSize` and **nothing else** — there is no search
   * parameter — so a screen on this read must not offer a search box that only sees the page.
   */
  listAccountsPage: build.query({
    query: ({ pageSize, cursor, unit }) => ({
      url: "/ledger/accounts",
      params: queryParams({ pageSize, cursor, unit })
    }),
    providesTags: (result) => providesList("LedgerAccount", result?.accounts, "accountId"),
    keepUnusedDataFor: 60
  }),
  /**
   * Addressed by **customerId**, not accountId — and it is the self-service call
   * too. `@RequiresPermissionWithOwnership` serves the customer's own view and the
   * ops view from this one path, which is why there is no `/ledger/accounts/me`.
   */
  /**
   * **Every** account one customer holds, one per book.
   *
   * The read a saver's own screens want: a customer running a gram plan and a
   * rupee plan holds two accounts, and neither `getAccount` (which needs the
   * unit and answers one) nor a tenant-wide list is the right shape for "show me
   * my savings". Shipped by Phase 2 and bound by nothing until now.
   *
   * Never summed. The caller renders one card, one statement and one chart per
   * entry — there is no figure that is the customer's total across books
   * (ADR-C10).
   */
  listCustomerAccounts: build.query({
    query: (customerId) => ({ url: `/ledger/customers/${customerId}/accounts` }),
    providesTags: (_r, _e, customerId) => [
      { type: "LedgerAccount", id: "LIST" },
      ...providesOne("LedgerAccount", customerId)
    ],
    keepUnusedDataFor: 60
  }),
  /**
   * One customer's account. **`unit` is required when the customer holds more
   * than one book** — ledger-service answers `422 LEDGER_UNIT_REQUIRED` rather
   * than picking one (INV-22), so a console that reads a both-books customer
   * without a scope gets an error, not the wrong account.
   */
  getAccount: build.query({
    query: (args) => {
      const { customerId, unit } = typeof args === "string" ? { customerId: args, unit: void 0 } : args;
      return { url: `/ledger/accounts/${customerId}`, params: queryParams({ unit }) };
    },
    providesTags: (_r, _e, args) => providesOne("LedgerAccount", typeof args === "string" ? args : args.customerId),
    keepUnusedDataFor: 60
  }),
  /**
   * Paged entries for one customer — the statement *is* a page of legs. No date
   * range: the controller binds only `cursor` and `pageSize`. The three balance
   * totals a statement header wants come from `getAccount`, which is why the
   * Accounts screen makes both calls.
   *
   * Provides the `LIST` tag as well as its own id: a reversal or a suspense
   * resolution rewrites *some* customer's statement without knowing whose, and
   * RTK only matches an invalidation against a tag that was actually provided —
   * so without `LIST` here those mutations could not reach a statement at all.
   */
  getAccountStatement: build.query({
    query: ({ customerId, pageSize, cursor, unit }) => ({
      url: `/ledger/accounts/${customerId}/statement`,
      params: queryParams({ pageSize, cursor, unit })
    }),
    ...cursorPagedEndpoint((entry) => entry.entryId),
    providesTags: (_r, _e, { customerId }) => [
      { type: "Statement", id: "LIST" },
      { type: "Statement", id: customerId }
    ]
  }),
  /**
   * The same statement, paged **discretely** — see {@link listEntriesPage} for why both shapes
   * exist.
   *
   * Unlike the journal read this one **does** strike a real `pagination.total`: its `WHERE`
   * carries `le.account_id` and is served by `idx_ledger_entries_tenant_account_id`, so the
   * aggregate is bounded by one customer's own history and may be paid on any page
   * (`LedgerEntryQueryRepositoryImpl` says so where it declines the journal's). A numbered pager
   * over this endpoint is therefore exact.
   */
  getAccountStatementPage: build.query({
    query: ({ customerId, pageSize, cursor, unit }) => ({
      url: `/ledger/accounts/${customerId}/statement`,
      params: queryParams({ pageSize, cursor, unit })
    }),
    providesTags: (_r, _e, { customerId }) => [
      { type: "Statement", id: "LIST" },
      { type: "Statement", id: customerId }
    ]
  }),
  /**
   * Its own envelope with `nextCursor` in the body, like `listAccounts` — not a
   * `PagedApiResponse` — so it accumulates through the same hand-rolled `merge`:
   * continuation pages append their legs under the first page's cache key, and
   * the scheme's name and whole-statement totals are taken from whichever page
   * arrived last (the server sends the same rollup on every page, so the latest
   * read is simply the freshest).
   */
  getSchemeStatement: build.query({
    query: ({ customerId, schemeEnrollmentId, pageSize, cursor }) => ({
      url: `/ledger/accounts/${customerId}/schemes/${schemeEnrollmentId}/statement`,
      params: queryParams({ pageSize, cursor })
    }),
    serializeQueryArgs: ({ queryArgs, endpointName }) => {
      const { cursor: _cursor, ...rest } = queryArgs ?? {};
      return `${endpointName}(${JSON.stringify(rest)})`;
    },
    merge: (currentCache, incoming, { arg }) => {
      if (!arg?.cursor) {
        Object.assign(currentCache, incoming);
        currentCache.nextCursor = incoming.nextCursor ?? null;
        return;
      }
      const { entries, nextCursor, ...rollup } = incoming;
      Object.assign(currentCache, rollup);
      mergeContinuationPage(currentCache.entries, entries, (entry) => entry.entryId);
      currentCache.nextCursor = nextCursor ?? null;
    },
    forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
    providesTags: (_r, _e, { schemeEnrollmentId }) => [
      { type: "Statement", id: "LIST" },
      { type: "Statement", id: schemeEnrollmentId }
    ]
  }),
  /**
   * The same plan statement, paged **discretely** — see {@link listEntriesPage} for why both
   * shapes exist.
   *
   * Its envelope is bespoke like `listAccounts`': the rollup (`instalmentsPaid`, both totals)
   * travels with every page and there is **no `pagination` block at all** — only `nextCursor`.
   * So a caller wiring a grid takes "there is more" from `nextCursor != null` and has no count
   * to show; the footer gets the walked lower bound rather than a number nobody struck.
   */
  getSchemeStatementPage: build.query({
    query: ({ customerId, schemeEnrollmentId, pageSize, cursor }) => ({
      url: `/ledger/accounts/${customerId}/schemes/${schemeEnrollmentId}/statement`,
      params: queryParams({ pageSize, cursor })
    }),
    providesTags: (_r, _e, { schemeEnrollmentId }) => [
      { type: "Statement", id: "LIST" },
      { type: "Statement", id: schemeEnrollmentId }
    ]
  }),
  /**
   * Invalidates by **customerId**, not the `accountId` the path takes: `getAccount`
   * is keyed by customerId, so an `accountId` tag only ever matched a list row and
   * the detail's status pill stayed on the old value until a full reload.
   */
  freezeAccount: build.mutation({
    query: ({ accountId, reason, requestId }) => ({
      url: `/ledger/accounts/${accountId}/freeze`,
      method: "POST",
      body: { reason },
      idempotencyKey: `account-freeze:${accountId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { customerId }) => invalidatesOne("LedgerAccount", customerId)
  }),
  /** No body — the controller declares no `@RequestBody`, so a reason would be discarded. */
  unfreezeAccount: build.mutation({
    query: ({ accountId, requestId }) => ({
      url: `/ledger/accounts/${accountId}/unfreeze`,
      method: "POST",
      idempotencyKey: `account-unfreeze:${accountId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { customerId }) => invalidatesOne("LedgerAccount", customerId)
  }),
  /** Both bounds are required — the controller's `@RequestParam LocalDate` pair has no default. */
  getDailyVolume: build.query({
    query: ({ from, to, unit }) => ({
      url: "/ledger/reports/daily-volume",
      params: queryParams({ from, to, unit })
    }),
    providesTags: [{ type: "DailyVolume", id: "LIST" }],
    keepUnusedDataFor: 300
  }),
  /**
   * Omitting `status` yields `[OPEN, ESCALATED]` — open exceptions only, not
   * everything. The array is sent comma-delimited, which is what Spring's
   * `List<SuspenseCaseStatusDto>` binding accepts.
   */
  listSuspenseCases: build.query({
    query: ({ pageSize, cursor, status }) => ({
      url: "/ledger/suspense-cases",
      params: queryParams({
        pageSize,
        cursor,
        status: status && status.length > 0 ? status.join(",") : void 0
      })
    }),
    ...cursorPagedEndpoint(
      (suspenseCase) => suspenseCase.suspenseCaseId
    ),
    providesTags: (result) => providesList("SuspenseCase", result, "suspenseCaseId")
  }),
  /**
   * The same queue, paged **discretely** — see {@link listEntriesPage} for why both shapes
   * exist.
   *
   * `SuspenseCaseRepositoryAdapter` pages by signed offset and strikes Spring Data's own
   * `getTotalElements()` on every page, so both the numbered pager and any badge reading
   * `pagination.total` are the tenant's figure for the statuses asked for — not the size of the
   * fetch.
   */
  listSuspenseCasesPage: build.query({
    query: ({ pageSize, cursor, status }) => ({
      url: "/ledger/suspense-cases",
      params: queryParams({
        pageSize,
        cursor,
        status: status && status.length > 0 ? status.join(",") : void 0
      })
    }),
    providesTags: (result) => providesList("SuspenseCase", result, "suspenseCaseId")
  }),
  /**
   * A resolution posts contra or reclassifying entries and moves the case to
   * `RESOLVED`, which is terminal (INV-12) — so the case id alone is a safe key.
   */
  resolveSuspenseCase: build.mutation({
    query: ({ id, resolutionType, correctCustomerId, note }) => ({
      url: `/ledger/suspense-cases/${id}/resolve`,
      method: "POST",
      body: {
        resolutionType,
        ...correctCustomerId ? { correctCustomerId } : {},
        ...note ? { note } : {}
      },
      idempotencyKey: `suspense-resolve:${id}`
    }),
    // Both arms post: RECLASSIFIED moves grams onto a customer account (and
    // its statement), REVERSED backs them out of suspense — either way the
    // live position and some statement changed.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("SuspenseCase", id),
      { type: "LedgerEntry", id: "LIST" },
      { type: "LedgerAccount", id: "LIST" },
      { type: "VaultPosition", id: "CURRENT" },
      { type: "Statement", id: "LIST" }
    ]
  }),
  /** No body — escalation records no note on this service. */
  escalateSuspenseCase: build.mutation({
    query: ({ id }) => ({
      url: `/ledger/suspense-cases/${id}/escalate`,
      method: "POST",
      idempotencyKey: `suspense-escalate:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("SuspenseCase", id)
  }),
  // ─── Vault (self-custody: our own vault at a partner bank, third-party audited) ──
  /**
   * The live four-way position read from `ledger_accounts.balance_grams` at
   * request time — where a reconciliation row is the same four figures frozen
   * as-of the end of a closed day. Takes no argument, so it is tagged by the
   * fixed `CURRENT` id and every mutation that posts (reverse, resolve)
   * invalidates that one tag.
   */
  getVaultPositions: build.query({
    query: (unit) => ({ url: "/ledger/vault/positions", params: queryParams({ unit: unit || void 0 }) }),
    providesTags: [{ type: "VaultPosition", id: "CURRENT" }]
  }),
  /**
   * What this tenant's book is denominated in — the catalogue behind every unit selector and every
   * formatter downstream of it. A staff read; the saver apps never call it, because a saver does
   * not choose a unit (each of their plans carries one).
   *
   * The `scale` and `symbol` here are the only sanctioned source of those two numbers: a console
   * that hard-codes "six decimals, suffix g" renders a rupee balance as a gram one.
   */
  listLedgerUnits: build.query({
    query: () => ({ url: "/ledger/units" }),
    providesTags: [{ type: "VaultPosition", id: "UNITS" }]
  }),
  /** A bounded recent-first log, not a page: `limit` only, no cursor and no date range. */
  listReconciliations: build.query({
    query: (args) => ({
      url: "/ledger/vault/reconciliations",
      params: queryParams({ limit: args?.limit, unit: args?.unit })
    }),
    providesTags: (result) => providesList("VaultReconciliation", result, "reconciliationId")
  }),
  /** Detail carries the contributing entries and the statement lines behind a variance. */
  getReconciliation: build.query({
    query: (id) => ({ url: `/ledger/vault/reconciliations/${id}` }),
    providesTags: (_r, _e, id) => providesOne("VaultReconciliation", id)
  }),
  /**
   * `date` is a query parameter and there is no body. Idempotent twice over: the
   * handler no-ops a date already reconciled for this tenant (INV-13) and returns
   * the existing row unchanged, so a date-derived key is exactly right.
   *
   * The response is the completed row, so a caller does not need to poll — but the
   * day must already be closed in the tenant reporting zone or the command rejects it.
   */
  runReconciliation: build.mutation({
    query: ({ date }) => ({
      url: "/ledger/vault/reconciliations/run",
      method: "POST",
      params: { date },
      idempotencyKey: `vault-recon:${date}`
    }),
    invalidatesTags: [
      { type: "VaultReconciliation", id: "LIST" },
      { type: "VaultStatement", id: "LIST" }
    ]
  }),
  /** Body field is `note`; `@NotBlank`, so an empty string is a 400 rather than a clear. */
  annotateReconciliation: build.mutation({
    query: ({ reconciliationId, note, requestId }) => ({
      url: `/ledger/vault/reconciliations/${reconciliationId}/annotate`,
      method: "POST",
      body: { note },
      idempotencyKey: `vault-recon-annotate:${reconciliationId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { reconciliationId }) => invalidatesOne("VaultReconciliation", reconciliationId)
  }),
  /** One optional exact `date`, not a range. Omit it for the most recent across all dates. */
  listVaultStatements: build.query({
    query: (args) => ({
      url: "/ledger/vault/statements",
      params: queryParams({ date: args?.date, unit: args?.unit })
    }),
    providesTags: (result) => providesList("VaultStatement", result, "statementId")
  }),
  uploadVaultStatement: build.mutation({
    query: ({ draftId, statementDate, source, statementRef, unit, quantity }) => ({
      url: "/ledger/vault/statements",
      method: "POST",
      body: { statementDate, source, statementRef, unit, quantity },
      // Keyed on the book as well as the day: a tenant reconciling both books
      // files one statement per book per date, and a key that named only the
      // date would make the second look like a replay of the first.
      idempotencyKey: `vault-statement:${statementDate}:${unit}:${draftId}`
    }),
    invalidatesTags: [{ type: "VaultStatement", id: "LIST" }]
  }),
  // ─── Bank custody (the tenant's own dedicated collection account — D1's (b′), D19) ──────
  //
  // The other half of Custody. Everything here takes the book explicitly: there is no "the
  // currency book" default, because a tenant may one day keep two and a screen that resolved the
  // book for itself would show one under the other's heading (INV-22).
  /**
   * The Trust-account tab's header: which account backs this book, whether it counts as external
   * evidence, and what the last reconciliation said.
   *
   * Tagged `CUSTODY` under `BankReconciliation` rather than given a tag of its own: every mutation
   * on this page moves it — a classification changes the queue count, a run changes the last
   * status — and one tag is what keeps the header from sitting stale beside a refreshed log.
   */
  getBankCustody: build.query({
    query: ({ unit }) => ({ url: "/ledger/bank/custody", params: { unit } }),
    providesTags: [{ type: "BankReconciliation", id: "CUSTODY" }]
  }),
  /** Imported statement periods, newest first. Lines are omitted here; `getBankStatement` carries them. */
  listBankStatements: build.query({
    query: ({ unit }) => ({ url: "/ledger/bank/statements", params: { unit } }),
    providesTags: (result) => providesList("BankStatement", result, "id")
  }),
  /** One statement with its lines — the classification queue an operator works through. */
  getBankStatement: build.query({
    query: (statementId) => ({ url: `/ledger/bank/statements/${statementId}` }),
    providesTags: (_r, _e, id) => providesOne("BankStatement", id)
  }),
  /**
   * Imports one period. The endpoint refuses a file whose lines do not add up to its own closing
   * balance, so a failure here is usually a column mapped to the wrong side rather than a rejection
   * of the data.
   *
   * Keyed on the book and the period as well as the draft: a tenant reconciling two books files one
   * statement per book per period, and a key naming only the period would make the second read as a
   * replay of the first — the same trap the vault statement key hit in Phase 5.
   */
  importBankStatement: build.mutation({
    query: ({ draftId, unit, periodStart, periodEnd, ...body }) => ({
      url: "/ledger/bank/statements",
      method: "POST",
      body: { unit, periodStart, periodEnd, ...body },
      idempotencyKey: `bank-statement:${unit}:${periodStart}:${periodEnd}:${draftId}`
    }),
    invalidatesTags: [
      { type: "BankStatement", id: "LIST" },
      { type: "BankReconciliation", id: "CUSTODY" }
    ]
  }),
  /**
   * Names what one line settles (D19(2)). Keyed on the line rather than a request id: a line has
   * one current classification, and a retried call that lands the same answer is the same fact.
   */
  classifyBankLine: build.mutation({
    query: ({ statementId, lineId, classification, note }) => ({
      url: `/ledger/bank/statements/${statementId}/lines/${lineId}/classify`,
      method: "POST",
      body: { classification, note },
      idempotencyKey: `bank-line-classify:${lineId}:${classification}`
    }),
    invalidatesTags: (_r, _e, { statementId }) => [
      ...invalidatesOne("BankStatement", statementId),
      { type: "BankReconciliation", id: "CUSTODY" }
    ]
  }),
  /** The (b′) reconciliation log for one book, newest first. */
  listBankReconciliations: build.query({
    query: ({ unit, limit }) => ({
      url: "/ledger/bank/reconciliations",
      params: queryParams({ unit, limit })
    }),
    providesTags: (result) => providesList("BankReconciliation", result, "id")
  }),
  /**
   * The ops re-run for a missed day. Idempotent twice over: a struck day is a no-op server-side
   * because a completed reconciliation is a historical fact, and the key is the date and book.
   */
  runBankReconciliation: build.mutation({
    query: ({ reconciliationDate, unit }) => ({
      url: "/ledger/bank/reconciliations/run",
      method: "POST",
      body: { reconciliationDate, unit },
      idempotencyKey: `bank-recon:${unit}:${reconciliationDate}`
    }),
    invalidatesTags: [
      { type: "BankReconciliation", id: "LIST" },
      { type: "BankReconciliation", id: "CUSTODY" }
    ]
  }),
  /** Explains a flagged day (D19(3)); never clears it. `note` is `@NotBlank`. */
  annotateBankReconciliation: build.mutation({
    query: ({ reconciliationId, note, requestId }) => ({
      url: `/ledger/bank/reconciliations/${reconciliationId}/annotate`,
      method: "POST",
      body: { note },
      idempotencyKey: `bank-recon-annotate:${reconciliationId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { reconciliationId }) => invalidatesOne("BankReconciliation", reconciliationId)
  }),
  /**
   * The body names the **date** of the reconciliation to match against, not its
   * id — `MatchVaultStatementCommand` resolves the day itself. A statement matches
   * once, so the statement id alone keys it.
   */
  matchVaultStatement: build.mutation({
    query: ({ statementId, reconciliationDate }) => ({
      url: `/ledger/vault/statements/${statementId}/match`,
      method: "POST",
      body: { reconciliationDate },
      idempotencyKey: `vault-statement-match:${statementId}`
    }),
    invalidatesTags: (_r, _e, { statementId }) => [
      ...invalidatesOne("VaultStatement", statementId),
      { type: "VaultReconciliation", id: "LIST" }
    ]
  })
});

// src/api.ts
var ledgerApi = createContextApi({
  context: "ledger",
  reducerPath: "ledgerApi",
  tagTypes: LEDGER_TAGS,
  endpoints: ledgerEndpoints
});
var LedgerUnitScopeContext = createContext(null);
function LedgerUnitScopeProvider({
  tenantId,
  app,
  children
}) {
  const key = storageKey({ tenantId, app });
  const [chosen, setChosenState] = useState(() => readRemembered(key));
  const setChosen = useCallback(
    (next) => {
      setChosenState(next);
      remember(key, next);
    },
    [key]
  );
  const value = useMemo(
    () => ({ tenantId, app, chosen, setChosen }),
    [tenantId, app, chosen, setChosen]
  );
  return createElement(LedgerUnitScopeContext.Provider, { value }, children);
}
function storageKey(config) {
  return config.tenantId ? `qb.unit-scope.${config.tenantId}.${config.app}` : null;
}
function readRemembered(key) {
  if (!key) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function remember(key, unit) {
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, unit);
  } catch {
  }
}
function defaultUnit(units) {
  const primary = units.find((entry) => entry.primary);
  if (primary) return primary.unit;
  return units.length > 0 ? units[0].unit : null;
}
function resolveScopedUnit(units, remembered) {
  if (units.length === 0) return null;
  const kept = units.find((entry) => entry.unit === remembered);
  return kept ? kept.unit : defaultUnit(units);
}
var METAL = "METAL";
function useLedgerUnits() {
  const config = useContext(LedgerUnitScopeContext);
  if (!config) {
    throw new Error("useLedgerUnits requires a <LedgerUnitScopeProvider> above it");
  }
  const { data, isLoading, isError } = ledgerApi.useListLedgerUnitsQuery();
  const units = useMemo(() => data ?? [], [data]);
  const unit = useMemo(() => resolveScopedUnit(units, config.chosen), [units, config.chosen]);
  const { setChosen } = config;
  const setUnit = useCallback((next) => setChosen(next), [setChosen]);
  const unitInfo = useCallback(
    (code) => units.find((entry) => entry.unit === code) ?? null,
    [units]
  );
  return useMemo(
    () => ({
      units,
      unit,
      selected: unit ? units.find((entry) => entry.unit === unit) ?? null : null,
      setUnit,
      isMultiUnit: units.length > 1,
      hasMetalUnit: units.some((entry) => entry.family === METAL),
      isLoading,
      isError,
      unitInfo
    }),
    [units, unit, setUnit, isLoading, isError, unitInfo]
  );
}

// src/vocabulary.ts
var METAL_VOCABULARY = {
  balance: "Gold balance",
  balanceShort: "Gold",
  custody: "Vault",
  custodyLong: "Vault custody",
  maturityCredit: "Redemption coupon",
  plan: "Savings scheme",
  planPlural: "Savings schemes",
  credit: "Gold credited",
  debit: "Gold debited",
  redemption: "Redemption",
  ledger: "Gold ledger",
  contribution: "Gold purchase"
};
var CURRENCY_VOCABULARY = {
  balance: "Amount saved",
  balanceShort: "Saved",
  custody: "Trust account",
  custodyLong: "Trust account",
  maturityCredit: "Purchase credit",
  plan: "Booking plan",
  planPlural: "Booking plans",
  credit: "Amount added",
  debit: "Amount used",
  redemption: "Purchase credit",
  ledger: "Savings ledger",
  contribution: "Deposit"
};
var UNKNOWN_VOCABULARY = {
  balance: "Balance",
  balanceShort: "Balance",
  custody: "Custody",
  custodyLong: "Custody account",
  maturityCredit: "Maturity credit",
  plan: "Savings plan",
  planPlural: "Savings plans",
  credit: "Credited",
  debit: "Debited",
  redemption: "Redemption",
  ledger: "Ledger",
  contribution: "Contribution"
};
var BY_FAMILY = {
  METAL: METAL_VOCABULARY,
  CURRENCY: CURRENCY_VOCABULARY
};
function vocabularyFor(family, overrides) {
  const base = family ? BY_FAMILY[family] : void 0;
  if (!base) {
    return overrides ? { ...UNKNOWN_VOCABULARY, ...overrides } : UNKNOWN_VOCABULARY;
  }
  return overrides ? { ...base, ...overrides } : base;
}
function useVocabulary() {
  const { selected } = useLedgerUnits();
  return vocabularyFor(selected?.family);
}
function useVocabularyFor(unit) {
  const { unitInfo } = useLedgerUnits();
  return vocabularyFor(unit ? unitInfo(unit)?.family : void 0);
}

// src/index.ts
var LEDGER_INVALIDATION_RULES = [
  {
    event: "payment.captured",
    invalidate: ({ customerId }) => [
      { type: "LedgerEntry", id: "LIST" },
      ...customerId ? [{ type: "LedgerAccount", id: customerId }] : [{ type: "LedgerAccount", id: "LIST" }],
      { type: "Statement", id: "LIST" },
      { type: "VaultPosition", id: "CURRENT" }
    ]
  },
  {
    event: "redemption.settled",
    invalidate: () => [
      { type: "LedgerEntry", id: "LIST" },
      { type: "LedgerAccount", id: "LIST" },
      { type: "Statement", id: "LIST" },
      { type: "VaultPosition", id: "CURRENT" }
    ]
  }
];
function registerLedgerInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: ledgerApi,
    rules: LEDGER_INVALIDATION_RULES
  });
}
var {
  useListEntriesQuery,
  useListEntriesPageQuery,
  useGetTransactionQuery,
  useReverseTransactionMutation,
  useListAccountsQuery,
  useListAccountsPageQuery,
  useListCustomerAccountsQuery,
  useGetAccountQuery,
  useGetAccountStatementQuery,
  useGetAccountStatementPageQuery,
  useGetSchemeStatementQuery,
  useGetSchemeStatementPageQuery,
  useFreezeAccountMutation,
  useUnfreezeAccountMutation,
  useGetDailyVolumeQuery,
  useListSuspenseCasesQuery,
  useListSuspenseCasesPageQuery,
  useResolveSuspenseCaseMutation,
  useEscalateSuspenseCaseMutation,
  useGetVaultPositionsQuery,
  useListLedgerUnitsQuery,
  useListReconciliationsQuery,
  useGetReconciliationQuery,
  useRunReconciliationMutation,
  useAnnotateReconciliationMutation,
  useListVaultStatementsQuery,
  useUploadVaultStatementMutation,
  useMatchVaultStatementMutation,
  useGetBankCustodyQuery,
  useListBankStatementsQuery,
  useGetBankStatementQuery,
  useImportBankStatementMutation,
  useClassifyBankLineMutation,
  useListBankReconciliationsQuery,
  useRunBankReconciliationMutation,
  useAnnotateBankReconciliationMutation
} = ledgerApi;

export { LEDGER_DEFAULT_PAGE_SIZE, LEDGER_DEFAULT_RECONCILIATION_LIMIT, LEDGER_INVALIDATION_RULES, LEDGER_MAX_PAGE_SIZE, LEDGER_TAGS, LedgerUnitScopeProvider, ledgerApi, ledgerEndpoints, registerLedgerInvalidation, resolveScopedUnit, useAnnotateBankReconciliationMutation, useAnnotateReconciliationMutation, useClassifyBankLineMutation, useEscalateSuspenseCaseMutation, useFreezeAccountMutation, useGetAccountQuery, useGetAccountStatementPageQuery, useGetAccountStatementQuery, useGetBankCustodyQuery, useGetBankStatementQuery, useGetDailyVolumeQuery, useGetReconciliationQuery, useGetSchemeStatementPageQuery, useGetSchemeStatementQuery, useGetTransactionQuery, useGetVaultPositionsQuery, useImportBankStatementMutation, useLedgerUnits, useListAccountsPageQuery, useListAccountsQuery, useListBankReconciliationsQuery, useListBankStatementsQuery, useListCustomerAccountsQuery, useListEntriesPageQuery, useListEntriesQuery, useListLedgerUnitsQuery, useListReconciliationsQuery, useListSuspenseCasesPageQuery, useListSuspenseCasesQuery, useListVaultStatementsQuery, useMatchVaultStatementMutation, useResolveSuspenseCaseMutation, useReverseTransactionMutation, useRunBankReconciliationMutation, useRunReconciliationMutation, useUnfreezeAccountMutation, useUploadVaultStatementMutation, useVocabulary, useVocabularyFor, vocabularyFor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map